import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GooglePlacesService from '../services/googlePlaces';
import Icon from './shared/Icon';
import PoweredByGoogle from './shared/PoweredByGoogle';

// Module-scoped so a city we already geocoded survives modal close/reopen and
// component remounts. Each entry is one city lookup (plus a place-details call
// when only Google's fallback knew the city) — keeping it across mounts saves
// them per cache hit. Keyed by trimmed input
// string. Unbounded but tiny (one entry per distinct city string the user has
// typed), so no eviction needed for a session.
const geocodeCache = {};

function PlaceAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder,
  locationBias = null, // Optional { lat, lng } to bias results toward a location
  locationString = null, // Optional location name to geocode for bias (e.g., "Paris, France")
  showCoordinateWarning = false // Show warning if coordinates couldn't be determined
}) {
  const { t } = useTranslation('search');
  const resolvedPlaceholder = placeholder ?? t('placeAutocomplete.placeholder');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [resolvedLocationBias, setResolvedLocationBias] = useState(locationBias);
  const [coordinateWarning, setCoordinateWarning] = useState(false);
  const debounceTimer = useRef(null);
  const geocodeTimer = useRef(null);

  // Sync input value with prop
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    // A lookup still in flight when the location changes or the field unmounts
    // must not set the bias afterwards.
    let cancelled = false;
    // If direct locationBias is provided, use it
    if (locationBias) {
      setResolvedLocationBias(locationBias);
      return;
    }

    // If locationString is provided, geocode it
    if (locationString && locationString.trim()) {
      const trimmed = locationString.trim();

      // Check module-scoped cache first to avoid duplicate API calls across remounts
      if (geocodeCache[trimmed]) {
        setResolvedLocationBias(geocodeCache[trimmed]);
        return;
      }

      // Clear existing timer
      if (geocodeTimer.current) {
        clearTimeout(geocodeTimer.current);
      }

      // A new place to look up: drop the previous one's bias until it answers,
      // so a string that doesn't resolve never keeps the last city's.
      setResolvedLocationBias(null);

      // Debounce geocoding
      geocodeTimer.current = setTimeout(async () => {
        try {
          const citySuggestions = await GooglePlacesService.getCityAutocompleteSuggestions(trimmed);
          if (cancelled) return;
          const top = Array.isArray(citySuggestions) ? citySuggestions[0] : null;
          if (!top) return;
          // A city GeoNames knows comes back from the local lookup with its
          // coordinates and NO place_id (spine surface audit A4). Its point is
          // trusted only when the prediction IS the string asked ("Annecy,
          // France" — the rule lookupCityCoords uses): a bare or region name
          // matches some town of that name anywhere ("Italy" is Louisa,
          // Kentucky), and "Paris, Texas" comes back as Paris, France because
          // the lookup ignores a region hint. A wrong 50 km bias is worse than
          // none. Google's fallback predictions still carry only a place_id.
          const localPoint = trimmed.includes(',')
            && String(top.description || '').trim().toLowerCase() === trimmed.toLowerCase()
            && Number.isFinite(top.latitude) && Number.isFinite(top.longitude);
          let location = localPoint ? { lat: top.latitude, lng: top.longitude } : null;
          if (!location && top.place_id) {
            const details = await GooglePlacesService.getPlaceDetails(top.place_id);
            if (cancelled) return;
            // Extract location from geometry.location (API returns nested structure)
            location = details?.geometry?.location || details?.location || null;
          }
          if (location) {
            geocodeCache[trimmed] = location;
            setResolvedLocationBias(location);
          }
        } catch (error) {
          console.warn('Could not geocode location for bias:', error);
        }
      }, 500);
    } else {
      setResolvedLocationBias(null);
    }

    return () => {
      cancelled = true;
      if (geocodeTimer.current) {
        clearTimeout(geocodeTimer.current);
      }
    };
  }, [locationBias, locationString]);

  // Search for places using Google Places Autocomplete
  const searchPlaces = async (input) => {
    if (!input || input.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const results = await GooglePlacesService.getPlaceAutocompleteSuggestions(input, resolvedLocationBias);

      if (results && results.length > 0) {
        setSuggestions(results);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setShowSuggestions(true);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  };

  // Handle suggestion selection - fetch place details for coordinates
  const handleSuggestionClick = async (suggestion) => {
    const fullText = suggestion.description;

    setInputValue(fullText);
    onChange(fullText);
    setShowSuggestions(false);
    setSuggestions([]);
    setCoordinateWarning(false);

    // Get place details including coordinates
    if (suggestion.place_id && onPlaceSelect) {
      try {
        const placeDetails = await GooglePlacesService.getPlaceDetails(suggestion.place_id);
        if (placeDetails) {
          // Extract location from geometry.location (API returns nested structure)
          const location = placeDetails.geometry?.location || placeDetails.location || null;
          const hasCoordinates = location && location.lat && location.lng;

          // Show warning if coordinates couldn't be determined and showCoordinateWarning is enabled
          if (!hasCoordinates && showCoordinateWarning) {
            setCoordinateWarning(true);
          }

          onPlaceSelect({
            name: placeDetails.name || fullText,
            address: placeDetails.formatted_address || placeDetails.address || fullText,
            location: location, // { lat, lng } or null
            placeId: suggestion.place_id
          });
        } else if (showCoordinateWarning) {
          // No place details returned
          setCoordinateWarning(true);
          onPlaceSelect({
            name: fullText,
            address: fullText,
            location: null,
            placeId: suggestion.place_id
          });
        }
      } catch (error) {
        console.error('Error getting place details:', error);
        if (showCoordinateWarning) {
          setCoordinateWarning(true);
        }
      }
    } else if (showCoordinateWarning) {
      // No place_id available, can't get coordinates
      setCoordinateWarning(true);
    }
  };

  // Handle input focus
  const handleFocus = () => {
    setShowSuggestions(true);
    if (inputValue && inputValue.length >= 2 && suggestions.length === 0) {
      searchPlaces(inputValue);
    }
  };

  // Handle input blur (with delay to allow clicks)
  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={resolvedPlaceholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="w-full p-3 border border-border rounded-lg bg-surface text-content-primary placeholder:text-content-muted focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2"
      />

      {/* Coordinate warning message */}
      {coordinateWarning && (
        <div className="mt-1 px-2 py-1.5 text-xs text-warning-dark bg-warning-light border border-warning/30 rounded-lg flex items-start gap-2">
          <Icon name="warning" className="w-4 h-4 text-warning flex-shrink-0" />
          <span>{t('placeAutocomplete.coordinateWarning')}</span>
        </div>
      )}

      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <div className="absolute top-full left-0 right-0 bg-surface border border-border rounded-lg shadow-elevation-2 z-modal mt-1 max-h-60 overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-2 text-content-secondary text-sm">
              <div className="inline-block w-3 h-3 border-2 border-primary/20 border-t-primary rounded-full animate-spin mr-2"></div>
              {t('placeAutocomplete.searchingLabel')}
            </div>
          )}

          {suggestions.map((suggestion, index) => {
            const mainText = suggestion.structured_formatting?.main_text || suggestion.description;
            const secondaryText = suggestion.structured_formatting?.secondary_text || '';

            return (
              <button
                key={suggestion.place_id || index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-4 py-3 border-b border-border/50 last:border-b-0 hover:bg-primary-lighter/30 focus-visible:bg-primary-lighter/30 focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2 transition-colors"
              >
                <div className="flex items-start">
                  <Icon name="mapPin" className="w-4 h-4 text-content-muted mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-content-primary font-medium truncate">{mainText}</div>
                    {secondaryText && (
                      <div className="text-sm text-content-secondary truncate">{secondaryText}</div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {!isLoading && suggestions.length === 0 && inputValue && inputValue.length >= 2 && (
            <div className="px-4 py-3 text-content-secondary text-sm">
              {t('placeAutocomplete.noResults')}
            </div>
          )}

          {/* Google autocomplete predictions — attribution inside the dropdown */}
          {suggestions.length > 0 && (
            <PoweredByGoogle variant="text" align="end" className="sticky bottom-0 bg-surface border-t border-border" />
          )}
        </div>
      )}
    </div>
  );
}

export default PlaceAutocomplete;
